function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === 'function') {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createCalendar, today, getLocalTimeZone } from "@internationalized/date";
import { useVirtualizer, defaultRangeExtractor } from "@tanstack/react-virtual";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCalendar, useDateFormatter, useLocale } from "react-aria";
import { useCalendarState } from "react-stately";
import { Box, Flex, Text } from "sprout-ui-primitives";
import { KeyboardArrowLeft, KeyboardArrowRight } from "sprout-ui-icons";
import styled from "styled-components";
import { themeGet } from "sprout-ui-theme";
import { differenceInMonths } from "date-fns";
import { ScrollContainerHiddenScroll } from "../ScrollContainer";
import { Button as BaseButton } from "../Button";
import CalendarMonth from "./CalendarMonth";
import useCustomWeekDays from "./useCustomWeekDays";
const Button = styled(BaseButton)`
  color: ${themeGet("colors.blue.40")};
  width: 32px;
  height: 32px;

  :disabled {
    color: ${themeGet("colors.blue.40")};
    opacity: 0.4;
  }
`;
const pageSize = 20;
const CalendarInfinite = (props)=>{
    const { defaultValue  } = props;
    const initialVisibleMonth = useMemo(()=>{
        if (defaultValue) {
            const timezone = getLocalTimeZone();
            const result = differenceInMonths(defaultValue.toDate(timezone), today(getLocalTimeZone()).toDate(timezone));
            if (result > 0) return result;
        }
        return 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [visibleMonths, setVisibleMonths] = useState(initialVisibleMonth > pageSize ? initialVisibleMonth : pageSize);
    const { locale  } = useLocale();
    const state = useCalendarState(_objectSpread({}, props, {
        locale,
        createCalendar,
        minValue: today(getLocalTimeZone()),
        visibleDuration: {
            months: visibleMonths
        }
    }));
    const { calendarProps  } = useCalendar(props, state);
    const timezone1 = getLocalTimeZone();
    const monthFormatter = useDateFormatter({
        month: "long",
        timeZone: timezone1
    });
    const headerFormatter = useDateFormatter({
        month: "long",
        timeZone: timezone1,
        year: "numeric"
    });
    const weekDays = useCustomWeekDays();
    const [scroll, setScroll] = useState(false);
    const handleScroll = useCallback((event)=>{
        const scrollTop = event.target.scrollTop;
        setScroll(scrollTop !== 0);
    }, []);
    //
    const [overscan, setOverscan] = useState(initialVisibleMonth > 2 ? initialVisibleMonth : 2);
    const indexRef = useRef(0);
    const scrollElementRef = useRef(null);
    const virtualizer = useVirtualizer({
        count: visibleMonths,
        estimateSize: useCallback(()=>288, []),
        getScrollElement: useCallback(()=>scrollElementRef.current, []),
        overscan,
        // Using negative number here is crucial! So that it calls `rangeExtractor` immediately
        scrollPaddingStart: -1,
        rangeExtractor: useCallback((range)=>{
            indexRef.current = range.startIndex;
            return defaultRangeExtractor(range);
        }, [])
    });
    const { scrollToIndex: scrollToIndexProp , getVirtualItems  } = virtualizer;
    // next, previous
    const scrollToIndex = useCallback((index, smoothScroll = true)=>{
        scrollToIndexProp(index, {
            align: "start",
            smoothScroll
        });
    }, [
        scrollToIndexProp
    ]);
    const previous = useCallback(()=>{
        scrollToIndex(Math.max(0, indexRef.current - 1));
    }, [
        scrollToIndex
    ]);
    const next = useCallback(()=>{
        scrollToIndex(indexRef.current + 1);
    }, [
        scrollToIndex
    ]);
    const reset = useCallback(()=>{
        scrollToIndex(0);
    }, [
        scrollToIndex
    ]);
    // "infinite scroll"
    const virtualItems = getVirtualItems();
    useEffect(()=>{
        const [lastItem] = virtualItems.reverse();
        if (!lastItem) return;
        if (lastItem.index >= visibleMonths - 1) setVisibleMonths((v)=>v + pageSize);
    }, [
        virtualItems,
        virtualizer,
        visibleMonths
    ]);
    //
    useEffect(()=>{
        if (initialVisibleMonth > 0) {
            scrollToIndex(initialVisibleMonth, false);
            if (overscan > 2) {
                const timer = setTimeout(()=>{
                    setOverscan(2);
                    clearTimeout(timer);
                });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return /*#__PURE__*/ _jsxs(Flex, {
        flexDirection: "column",
        overflow: "hidden",
        children: [
            /*#__PURE__*/ _jsxs(Box, {
                borderBottomWidth: "1px",
                borderBottomStyle: "solid",
                borderBottomColor: scroll ? "white.40" : "transparent",
                children: [
                    /*#__PURE__*/ _jsxs(Flex, {
                        height: "32px",
                        alignItems: "center",
                        pl: 3,
                        pr: 2,
                        justifyContent: "space-between",
                        children: [
                            /*#__PURE__*/ _jsx(Text, {
                                fontSize: "15px",
                                fontWeight: "medium",
                                color: "blue.40",
                                mx: 0,
                                children: headerFormatter.format(state.visibleRange.start.add({
                                    months: indexRef.current
                                }).toDate(timezone1)).toUpperCase()
                            }),
                            /*#__PURE__*/ _jsxs(Flex, {
                                children: [
                                    /*#__PURE__*/ _jsx(Button, {
                                        variant: "circle-bright-blue-bg",
                                        onClick: previous,
                                        disabled: indexRef.current === 0,
                                        children: /*#__PURE__*/ _jsx(KeyboardArrowLeft, {})
                                    }),
                                    /*#__PURE__*/ _jsx(Button, {
                                        variant: "circle-bright-blue-bg",
                                        onClick: reset,
                                        disabled: indexRef.current === 0,
                                        children: /*#__PURE__*/ _jsx(Box, {
                                            as: "span",
                                            borderRadius: "50%",
                                            borderWidth: "2px",
                                            borderStyle: "solid",
                                            borderColor: "inherit",
                                            width: "10px",
                                            height: "10px"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Button, {
                                        variant: "circle-bright-blue-bg",
                                        onClick: next,
                                        children: /*#__PURE__*/ _jsx(KeyboardArrowRight, {})
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Flex, {
                        height: "36px",
                        alignItems: "center",
                        px: 3,
                        children: weekDays.map((day, index)=>/*#__PURE__*/ _jsx(Flex, {
                                alignItems: "center",
                                justifyContent: "center",
                                flex: "1 0 0",
                                children: /*#__PURE__*/ _jsx(Text, {
                                    color: "white.100",
                                    fontWeight: "bold",
                                    children: day
                                })
                            }, index))
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ScrollContainerHiddenScroll, _objectSpread({}, calendarProps, {
                position: "relative",
                onScroll: handleScroll,
                px: 3,
                ref: scrollElementRef,
                style: {
                    height: virtualizer.getTotalSize()
                },
                children: virtualItems.map((virtualItem)=>{
                    const { index , measureElement  } = virtualItem;
                    const calendarMonth = state.visibleRange.start.add({
                        months: index
                    });
                    return /*#__PURE__*/ _jsxs(Flex, {
                        flexDirection: "column",
                        pt: index === 0 ? 0 : "9px",
                        ref: measureElement,
                        style: {
                            scrollSnapAlign: "start",
                            position: "absolute",
                            transform: `translate3d(0,${virtualItem.start}px,0)`
                        },
                        children: [
                            index !== 0 && /*#__PURE__*/ _jsx(Text, {
                                fontSize: "15px",
                                fontWeight: "medium",
                                color: "blue.40",
                                mx: 0,
                                children: monthFormatter.format(calendarMonth.toDate(timezone1)).toUpperCase()
                            }),
                            /*#__PURE__*/ _jsx(CalendarMonth, {
                                state: state,
                                startDate: calendarMonth,
                                variant: "infinite"
                            })
                        ]
                    }, index);
                })
            }))
        ]
    });
};
export default CalendarInfinite;
